// Helpers for embedding YouTube videos on Video slides.
//
// Parsing is intentionally strict: we only accept the two canonical share
// URL shapes (youtube.com/watch?v=<id> and youtu.be/<id>) so that a saved
// YouTube slide always resolves to a single, embeddable video id.

const VIDEO_ID_RE = /^[A-Za-z0-9_-]{11}$/

function parseUrl(url: string | null | undefined): URL | null {
  if (!url) return null
  try {
    return new URL(url.trim())
  } catch {
    return null
  }
}

/**
 * Extract the 11-character video id from a YouTube watch or short-link URL.
 * Accepts youtube.com/watch?v=<id> and youtu.be/<id> (incl. www./m. hosts).
 * Returns null for anything else (embed/shorts/playlist-only/bare id/etc.).
 */
export function getYouTubeVideoId(
  url: string | null | undefined
): string | null {
  const parsed = parseUrl(url)
  if (!parsed) return null

  const host = parsed.hostname.toLowerCase().replace(/^(www|m)\./, '')

  let id: string | null = null
  if (host === 'youtube.com') {
    if (parsed.pathname === '/watch') id = parsed.searchParams.get('v')
  } else if (host === 'youtu.be') {
    id = parsed.pathname.slice(1)
  }

  return id && VIDEO_ID_RE.test(id) ? id : null
}

function parseTimeToSeconds(raw: string): number | null {
  const trimmed = raw.trim()
  if (/^\d+$/.test(trimmed)) {
    const n = parseInt(trimmed, 10)
    return n > 0 ? n : null
  }
  // Supports 1h2m3s / 1m30s / 90s combinations.
  const match = trimmed.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/)
  if (!match) return null
  const [, h, m, s] = match
  if (!h && !m && !s) return null
  const seconds =
    parseInt(h || '0', 10) * 3600 +
    parseInt(m || '0', 10) * 60 +
    parseInt(s || '0', 10)
  return seconds > 0 ? seconds : null
}

/**
 * Extract a start offset (in whole seconds) from a YouTube URL's `t`/`start`
 * param. Supports `90`, `90s`, and `1m30s`-style values. Returns null when
 * absent or unparseable.
 */
export function getYouTubeStartSeconds(
  url: string | null | undefined
): number | null {
  const parsed = parseUrl(url)
  if (!parsed) return null
  const raw = parsed.searchParams.get('t') ?? parsed.searchParams.get('start')
  return raw ? parseTimeToSeconds(raw) : null
}

/**
 * Return the URL with its start offset (`t` param) set to `seconds`, or removed
 * when `seconds` is null/<=0. Preserves the rest of the URL. Returns the input
 * unchanged if it can't be parsed.
 */
export function setYouTubeStartSeconds(
  url: string,
  seconds: number | null
): string {
  const parsed = parseUrl(url)
  if (!parsed) return url
  parsed.searchParams.delete('start')
  if (seconds && seconds > 0) {
    parsed.searchParams.set('t', String(Math.floor(seconds)))
  } else {
    parsed.searchParams.delete('t')
  }
  return parsed.toString()
}

/**
 * Build a privacy-friendly (nocookie) embed URL for the given video id.
 */
export function buildYouTubeEmbedUrl(
  videoId: string,
  opts: { autoplay?: boolean; startSeconds?: number | null } = {}
): string {
  const params = new URLSearchParams({
    rel: '0',
    modestbranding: '1',
    playsinline: '1',
  })
  if (opts.autoplay) params.set('autoplay', '1')
  if (opts.startSeconds && opts.startSeconds > 0) {
    params.set('start', String(Math.floor(opts.startSeconds)))
  }
  return `https://www.youtube-nocookie.com/embed/${videoId}?${params.toString()}`
}
